import React, { useMemo } from 'react';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import { SearchableSelect, Input } from '../../../../components/ui';
import { ANALYTICS_COPY } from '../../constants/analyticsCopy';
import { FILTER_OPERATORS } from '../../constants/filterOperators';
import { createEmptyFilter } from '../../utils/reportFilters';
import useFieldFilterValues from '../../hooks/useFieldFilterValues';
import useInterveneFilterValues from '../../hooks/useInterveneFilterValues';
import { DATASET_INTERVENE } from '../../constants/datasetScope';

function FilterValueInput({
  fieldId,
  campaignIds,
  leadsFormIds,
  dataset,
  orgId,
  value,
  onChange,
  disabled,
}) {
  const isIntervene = dataset === DATASET_INTERVENE;
  const records = useFieldFilterValues(
    !isIntervene ? fieldId : '',
    campaignIds,
    leadsFormIds
  );
  const intervene = useInterveneFilterValues(isIntervene ? fieldId : '', orgId);
  const values = isIntervene ? intervene.values : records.values;
  const isLoading = isIntervene ? intervene.isLoading : records.isLoading;
  const options = useMemo(
    () => values.map((item) => ({ id: item, label: item })),
    [values]
  );

  if (fieldId && options.length) {
    return (
      <SearchableSelect
        label={ANALYTICS_COPY.filterValueLabel}
        placeholder={ANALYTICS_COPY.filterValuePlaceholder}
        options={options}
        value={value ? { id: value, label: value } : null}
        onChange={(opt) => onChange(opt?.id || '')}
        getOptionLabel={(opt) => opt.label}
        isOptionEqualToValue={(a, b) => a?.id === b?.id}
        disabled={disabled}
        loading={isLoading}
      />
    );
  }

  return (
    <Input
      label={ANALYTICS_COPY.filterValueLabel}
      placeholder={ANALYTICS_COPY.filterValuePlaceholder}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    />
  );
}

function FilterRow({
  filter,
  fieldOptions,
  campaignIds,
  leadsFormIds,
  dataset,
  orgId,
  onChange,
  onRemove,
  disabled,
  canRemove,
}) {
  const operatorOptions = FILTER_OPERATORS.map((item) => ({ id: item.id, label: item.label }));

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', md: 'flex-start' }}
      >
        <Typography
          variant="body2"
          fontWeight={700}
          color="text.secondary"
          sx={{ pt: { md: 2 }, minWidth: 52 }}
        >
          Where
        </Typography>
        <Box sx={{ flex: 1.2, minWidth: 0 }}>
          <SearchableSelect
            label={ANALYTICS_COPY.filterFieldLabel}
            placeholder={ANALYTICS_COPY.filterFieldPlaceholder}
            options={fieldOptions}
            value={fieldOptions.find((o) => o.id === filter.field) || null}
            onChange={(opt) => onChange({ field: opt?.id || '', value: '' })}
            getOptionLabel={(opt) => opt.label}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            disabled={disabled}
          />
        </Box>
        <Box sx={{ flex: 0.9, minWidth: 0 }}>
          <SearchableSelect
            label={ANALYTICS_COPY.filterOperatorLabel}
            options={operatorOptions}
            value={operatorOptions.find((o) => o.id === filter.operator) || operatorOptions[0]}
            onChange={(opt) => onChange({ operator: opt?.id || 'eq' })}
            getOptionLabel={(opt) => opt.label}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            disabled={disabled || !filter.field}
          />
        </Box>
        <Box sx={{ flex: 1.2, minWidth: 0 }}>
          <FilterValueInput
            fieldId={filter.field}
            campaignIds={campaignIds}
            leadsFormIds={leadsFormIds}
            dataset={dataset}
            orgId={orgId}
            value={filter.value}
            onChange={(nextValue) => onChange({ value: nextValue })}
            disabled={disabled || !filter.field}
          />
        </Box>
        {canRemove ? (
          <IconButton
            aria-label={ANALYTICS_COPY.removeFilterLabel}
            onClick={onRemove}
            disabled={disabled}
            sx={{ mt: { xs: 0, md: 0.5 }, alignSelf: { sm:'flex-end', md: 'center' } }}
          >
            <DeleteOutlineOutlinedIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Stack>
    </Box>
  );
}

export default function ReportFiltersPanel({
  filters = [],
  fieldOptions = [],
  campaignIds = [],
  leadsFormIds = [],
  dataset = '',
  orgId = '',
  onChange,
  disabled = false,
  embedded = false,
  excludeFieldIds = [],
  disclosure = false,
}) {
  const availableFields = useMemo(
    () => fieldOptions.filter((item) => !excludeFieldIds.includes(item.id)),
    [fieldOptions, excludeFieldIds]
  );
  const activeFilters = filters.length ? filters : [];

  const handleAddFilter = () => {
    onChange([...activeFilters, createEmptyFilter()]);
  };

  const handleUpdateFilter = (filterId, patch) => {
    onChange(activeFilters.map((item) => (
      item.id === filterId ? { ...item, ...patch } : item
    )));
  };

  const handleRemoveFilter = (filterId) => {
    onChange(activeFilters.filter((item) => item.id !== filterId));
  };

  const body = (
      <Stack spacing={1.5} sx={{ p: embedded ? 0 : { xs: 2, sm: 2.5 } }}>
        {activeFilters.map((filter) => (
          <FilterRow
            key={filter.id}
            filter={filter}
            fieldOptions={availableFields}
            campaignIds={campaignIds}
            leadsFormIds={leadsFormIds}
            dataset={dataset}
            orgId={orgId}
            onChange={(patch) => handleUpdateFilter(filter.id, patch)}
            onRemove={() => handleRemoveFilter(filter.id)}
            disabled={disabled}
            canRemove
          />
        ))}

        <Button
          variant="text"
          startIcon={<AddOutlinedIcon />}
          onClick={handleAddFilter}
          disabled={disabled || !availableFields.length}
          sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600, px: 0.5 }}
        >
          {ANALYTICS_COPY.addFilterLabel}
        </Button>
      </Stack>
  );

  if (disclosure) {
    return (
      <Box>
        {activeFilters.length ? (
          <Stack spacing={1.5} sx={{ mb: 1 }}>
            {activeFilters.map((filter) => (
              <FilterRow
                key={filter.id}
                filter={filter}
                fieldOptions={availableFields}
                campaignIds={campaignIds}
                leadsFormIds={leadsFormIds}
                dataset={dataset}
                orgId={orgId}
                onChange={(patch) => handleUpdateFilter(filter.id, patch)}
                onRemove={() => handleRemoveFilter(filter.id)}
                disabled={disabled}
                canRemove
              />
            ))}
          </Stack>
        ) : null}
        <Button
          variant="text"
          startIcon={<AddOutlinedIcon />}
          onClick={handleAddFilter}
          disabled={disabled}
          sx={{ textTransform: 'none', fontWeight: 600, px: 0.5 }}
        >
          {ANALYTICS_COPY.addFilterLabel}
        </Button>
      </Box>
    );
  }

  if (embedded) {
    return (
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <FilterListOutlinedIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" fontWeight={600}>
            {ANALYTICS_COPY.filtersSectionTitle}
          </Typography>
        </Stack>
        {body}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <FilterListOutlinedIcon color="primary" sx={{ mt: 0.25 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              {ANALYTICS_COPY.filtersSectionTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {ANALYTICS_COPY.filtersSectionSubtitle}
            </Typography>
          </Box>
        </Stack>
      </Box>
      {body}
    </Box>
  );
}
